import { useState, useEffect, useCallback, useRef } from 'react';
import { submitScore, recordCellAnswer } from '../lib/db';
import logger from '../lib/logger';
import {
  trackPuzzleStarted, trackPuzzleCompleted,
  trackCellCorrect, trackCellWrong,
  trackHintUsed,
} from '../lib/analytics';

const MAX_GUESSES          = 12;
const HINT_COST            = 1;
const MAX_HINTS            = 3;
const NO_SUGGESTIONS_BONUS = 20;

// ── Storage key ──────────────────────────────────────────────────────────────
const storageKey = (puzzleDate) => `htg_game_${puzzleDate}`;

// ── Diacritic normalization ──────────────────────────────────────────────────
export function normalizeName(str) {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'd')
    .replace(/ł/g, 'l')
    .replace(/Ł/g, 'l')
    .trim();
}

// ── Load / save helpers ──────────────────────────────────────────────────────
function loadState(puzzleDate) {
  try {
    const raw = localStorage.getItem(storageKey(puzzleDate));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveState(puzzleDate, state) {
  try {
    localStorage.setItem(storageKey(puzzleDate), JSON.stringify(state));
  } catch {}
}

function clearState(puzzleDate) {
  try {
    localStorage.removeItem(storageKey(puzzleDate));
  } catch {}
}

// ── Derive initial state ─────────────────────────────────────────────────────
function getInitialState(saved) {
  if (!saved) return {
    grid:                Array(3).fill(null).map(() => Array(3).fill(null)),
    hintShown:           Array(3).fill(null).map(() => Array(3).fill(false)),
    guessesUsed:         0,
    guessLog:            [],
    score:               0,
    leaderboardSubmitted: false,
    correctCount:   0,
    usedPlayers:    new Set(),
    hintsUsed:      0,
    currentStreak:  0,
    gameOver:       false,
    gameResult:     null,
    elapsedSeconds: 0,
    suggestionsOff: false,
  };
  return {
    grid:                 saved.grid,
    hintShown:            saved.hintShown,
    guessesUsed:          saved.guessesUsed,
    guessLog:             saved.guessLog,
    score:                saved.score,
    correctCount:         saved.correctCount,
    usedPlayers:          new Set(saved.usedPlayers),
    hintsUsed:            saved.hintsUsed,
    currentStreak:        saved.currentStreak,
    gameOver:             saved.gameOver,
    gameResult:           saved.gameResult,
    elapsedSeconds:       saved.elapsedSeconds || 0,
    suggestionsOff:       saved.suggestionsOff || false,
    leaderboardSubmitted: saved.leaderboardSubmitted || false,
  };
}

// ── Hook ─────────────────────────────────────────────────────────────────────
export function useGame(puzzle, suggestionsOffProp = false, testMode = false) {
  const puzzleDate = puzzle?.publish_date;
  const saved      = !testMode && puzzleDate ? loadState(puzzleDate) : null;
  const init       = getInitialState(saved);

  const [grid,           setGrid]           = useState(init.grid);
  const [hintShown,      setHintShown]      = useState(init.hintShown);
  const [guessesUsed,    setGuessesUsed]    = useState(init.guessesUsed);
  const [guessLog,       setGuessLog]       = useState(init.guessLog);
  const [activeCell,     setActiveCell]     = useState(null);
  const [score,          setScore]          = useState(init.score);
  const [correctCount,   setCorrectCount]   = useState(init.correctCount);
  const [usedPlayers,    setUsedPlayers]    = useState(init.usedPlayers);
  const [hintsUsed,      setHintsUsed]      = useState(init.hintsUsed);
  const [currentStreak,  setCurrentStreak]  = useState(init.currentStreak);
  const [gameOver,       setGameOver]       = useState(init.gameOver);
  const [gameResult,     setGameResult]     = useState(init.gameResult);
  const [elapsedSeconds, setElapsedSeconds] = useState(init.elapsedSeconds);
  const [suggestionsOff, setSuggestionsOff] = useState(saved?.suggestionsOff ?? suggestionsOffProp);
  const [toast,          setToast]          = useState(null);
  const [leaderboardSubmitted, setLeaderboardSubmitted] = useState(init.leaderboardSubmitted || false);

  const cellStartTime = useRef(null);
  const timerRef      = useRef(null);

  // ── Timer: pause on tab hide, resume on show (option 3) ──────────────────
  useEffect(() => {
    if (!puzzle || gameOver || testMode) return;

    timerRef.current = setInterval(() => setElapsedSeconds(s => s + 1), 1000);

    const onVisibilityChange = () => {
      if (document.hidden) {
        clearInterval(timerRef.current);
      } else {
        timerRef.current = setInterval(() => setElapsedSeconds(s => s + 1), 1000);
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [puzzle, gameOver, testMode]);

  // ── Persist state after every meaningful change ───────────────────────────
  useEffect(() => {
    if (testMode || !puzzleDate || (guessesUsed === 0 && !gameOver)) return;
    saveState(puzzleDate, {
      grid, hintShown, guessesUsed, guessLog, score, correctCount,
      usedPlayers: [...usedPlayers],
      hintsUsed, currentStreak, gameOver, gameResult, elapsedSeconds,
      suggestionsOff, leaderboardSubmitted,
    });
  }, [
    grid, hintShown, guessesUsed, guessLog, score, correctCount,
    usedPlayers, hintsUsed, currentStreak, gameOver, gameResult,
    elapsedSeconds, suggestionsOff, leaderboardSubmitted, puzzleDate, testMode,
  ]);

  const showToast = useCallback((msg, duration = 2800) => {
    setToast(msg);
    setTimeout(() => setToast(null), duration);
  }, []);

  const guessesLeft = MAX_GUESSES - guessesUsed;

  // ── Select cell ──────────────────────────────────────────────────────────
  const selectCell = useCallback((r, c) => {
    if (gameOver) return;
    if (grid[r][c]?.correct) return;
    setActiveCell({ r, c });
    cellStartTime.current = Date.now();
  }, [gameOver, grid]);

  // ── Submit answer ────────────────────────────────────────────────────────
  const submitAnswer = useCallback((answer) => {
    if (!activeCell || gameOver) return;
    if (answer.trim().length < 2) return;
    if (guessesLeft <= 0) return;

    const { r, c } = activeCell;
    const answerNorm   = normalizeName(answer.trim());
    const cell         = puzzle.puzzle_cells.find(pc => pc.row_index === r && pc.col_index === c);
    const validAnswers = cell?.valid_answers || [];
    const matchedValid = validAnswers.find(v => normalizeName(v) === answerNorm);
    const isCorrect    = !!matchedValid;

    if (usedPlayers.has(answerNorm)) {
      const isSoleAnswer = validAnswers.length === 1 && matchedValid;
      if (!isSoleAnswer) { showToast('⚠️ Already used that player in another cell'); return; }
    }

    if (guessesUsed === 0 && !testMode) {
      trackPuzzleStarted({
        puzzleNumber: puzzle.puzzle_number,
        puzzleTitle:  puzzle.title,
        isArchive:    puzzle.publish_date !== new Date().toISOString().split('T')[0],
      });
    }

    setGuessesUsed(g => g + 1);
    setGuessLog(log => [...log, isCorrect ? null : 'wrong']);

    if (isCorrect) {
      const newUsed = new Set(usedPlayers);
      newUsed.add(answerNorm);
      setUsedPlayers(newUsed);

      const newGrid = grid.map(row => [...row]);
      newGrid[r][c] = { answer: matchedValid, correct: true };
      setGrid(newGrid);

      const newCorrect  = correctCount + 1;
      setCorrectCount(newCorrect);

      const newStreak = currentStreak + 1;
      setCurrentStreak(newStreak);

      const cellSecs    = cellStartTime.current ? (Date.now() - cellStartTime.current) / 1000 : 99;
      const speedBonus  = cellSecs < 10 ? 100 : cellSecs < 20 ? 60 : cellSecs < 40 ? 30 : cellSecs < 60 ? 10 : 0;
      const noSugBonus  = suggestionsOff ? NO_SUGGESTIONS_BONUS : 0;
      const pts         = 100 + speedBonus + noSugBonus;
      const streakBonus = newStreak === 3 ? 50 : newStreak === 4 ? 150 : newStreak >= 5 ? 300 : 0;
      setScore(s => s + pts + streakBonus);

      if (puzzle?.id && !testMode) recordCellAnswer(puzzle.id, r, c, answerNorm).catch(() => {});

      if (!testMode) trackCellCorrect({
        puzzleNumber: puzzle.puzzle_number,
        row: r, col: c,
        timeSeconds:  Math.round(cellSecs),
        streakLength: newStreak,
      });

      if (streakBonus > 0) {
        const msg = newStreak === 3 ? `🔥 Hat-trick! +${streakBonus} streak bonus`
                  : newStreak === 4 ? `⚡ On fire! +${streakBonus} streak bonus`
                  : `🌟 Unstoppable! +${streakBonus} streak bonus`;
        setTimeout(() => showToast(msg), 500);
      } else {
        showToast(`✅ Correct! +${pts} pts`);
      }

      if (newCorrect === 9) {
        setTimeout(() => endGame(true, newCorrect, guessesUsed + 1, score + pts + streakBonus), 600);
      }
    } else {
      setCurrentStreak(0);
      showToast('❌ Not valid for this cell — try someone else');
      if (!testMode) trackCellWrong({ puzzleNumber: puzzle.puzzle_number, row: r, col: c });
    }

    const newGuessesUsed = guessesUsed + 1;
    if (newGuessesUsed >= MAX_GUESSES && correctCount < 9) {
      setTimeout(() => endGame(false, correctCount, newGuessesUsed, score), 600);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCell, gameOver, guessesLeft, usedPlayers, puzzle, grid, correctCount, currentStreak, score, guessesUsed, showToast, suggestionsOff]);

  // ── Use hint ─────────────────────────────────────────────────────────────
  const useHint = useCallback(() => {
    if (!activeCell || gameOver) { showToast('💡 Select a cell first'); return; }
    if (guessesLeft < HINT_COST)  { showToast('⚠️ Not enough guesses for a hint'); return; }
    if (hintsUsed >= MAX_HINTS)   { showToast('💡 No hints remaining'); return; }

    const { r, c } = activeCell;
    if (grid[r][c]?.correct) { showToast('💡 Cell already solved'); return; }
    if (hintShown[r][c])     { showToast('💡 Hint already shown for this cell'); return; }

    setGuessesUsed(g => g + HINT_COST);
    setGuessLog(log => [...log, 'hint']);
    setHintsUsed(h => h + 1);
    if (!testMode) trackHintUsed({ puzzleNumber: puzzle.puzzle_number, row: r, col: c, hintsUsedTotal: hintsUsed + 1 });
    setHintShown(prev => {
      const next = prev.map(row => [...row]);
      next[r][c] = true;
      return next;
    });

    showToast(`💡 Hint revealed (−${HINT_COST} guess)`);

    const newGuessesUsed = guessesUsed + HINT_COST;
    if (newGuessesUsed >= MAX_GUESSES && correctCount < 9) {
      setTimeout(() => endGame(false, correctCount, newGuessesUsed, score), 600);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCell, gameOver, guessesLeft, hintsUsed, grid, hintShown, guessesUsed, correctCount, score, showToast]);

  // ── End game ─────────────────────────────────────────────────────────────
  const endGame = useCallback((won, finalCorrect, finalGuesses, finalScore) => {
    clearInterval(timerRef.current);

    const remaining  = MAX_GUESSES - finalGuesses;
    const mult       = remaining >= 3 ? 2.0 : remaining >= 2 ? 1.5 : remaining >= 1 ? 1.25 : 1.0;
    const totalScore = Math.round(finalScore * mult);
    setScore(totalScore);

    if (!testMode) trackPuzzleCompleted({
      puzzleNumber:  puzzle?.puzzle_number,
      outcome:       won ? 'won' : 'lost',
      correctCount:  finalCorrect,
      guessesUsed:   finalGuesses,
      score:         totalScore,
      multiplier:    mult,
      suggestionsOff,
    });

    setGameOver(true);
    setActiveCell(null);
    setGameResult({ outcome: won ? 'won' : 'lost', multiplier: mult, finalScore: totalScore });

    // Clear persisted state — game finished
    if (!testMode && puzzleDate) clearState(puzzleDate);
  }, [puzzle, suggestionsOff, testMode, puzzleDate]);

  // ── Submit to leaderboard ────────────────────────────────────────────────
  const submitToLeaderboard = useCallback(async (username, userId = null) => {
    if (!puzzle?.id || testMode) return;
    try {
      await submitScore({
        puzzleId:    puzzle.id,
        userId,
        username,
        score,
        correctCount,
        guessesUsed,
        timeSeconds: elapsedSeconds,
      });
      setLeaderboardSubmitted(true); // triggers persist effect which saves to localStorage
    } catch (e) {
      logger.error('Score submit error:', e);
    }
  }, [puzzle, score, correctCount, guessesUsed, elapsedSeconds, testMode]);

  // ── Toggle suggestions (persisted) ──────────────────────────────────────
  const toggleSuggestions = useCallback(() => setSuggestionsOff(v => !v), []);

  return {
    grid, hintShown, guessLog, activeCell, score, correctCount,
    usedPlayers, hintsUsed, gameOver, gameResult, elapsedSeconds,
    toast, guessesLeft, guessesUsed,
    maxGuesses:    MAX_GUESSES,
    maxHints:      MAX_HINTS,
    hintCost:      HINT_COST,
    suggestionsOff,
    noSugBonus:    NO_SUGGESTIONS_BONUS,
    isRestored:    !!saved,
    leaderboardSubmitted,
    showToast,
    selectCell, submitAnswer, useHint, submitToLeaderboard, toggleSuggestions,
  };
}
